import useGetDeliverableConfigurators from '@entities/deliverableConfigurator/lib/hooks/useGetDeliverableConfigurators';
import { DeliverableFormats, ILocalDeliverable } from '@entities/deliverableFormats/ui/DeliverableFormats';
import { formatInrDisplayAmount } from '@shared/lib/utils/inrAmountInWords';
import { DeliverablePillSummary } from '@shared/ui';
import { Button } from '@shared/ui/Button';
import { Checkbox } from '@shared/ui/Checkbox';
import { Card, Divider, Empty, Flex, Form, Input, Upload } from 'antd';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, UploadCloud } from 'lucide-react';
import React, { useMemo } from 'react';
import { ICampaignAlterationApprovalRequest } from '../lib/types';
import { getBrandBreakdown } from './ChangeCommercialsForm';
import styles from './styles.module.scss';
import {
  useChangeDeliverablesForm
} from './useChangeDeliverablesForm';

interface ChangeDeliverablesFormProps {
  creatorName: string;
  campaignInfluencerId: string;
  briefId?: string;
  onSuccess?: () => void;
  onCancel: () => void;
  viewOnly?: boolean;
  alterationRequest?: ICampaignAlterationApprovalRequest;
}

export const ChangeDeliverablesForm: React.FC<ChangeDeliverablesFormProps> = ({
  creatorName,
  campaignInfluencerId,
  briefId,
  onSuccess,
  onCancel,
  viewOnly = false,
  alterationRequest,
}) => {
  const {
    influencerDirection,
    setInfluencerDirection,
    brandDirection,
    setBrandDirection,
    influencerDelta,
    setInfluencerDelta,
    brandDelta,
    setBrandDelta,
    remarks,
    setRemarks,
    tax,
    setTax,
    showAdvancedTax,
    setShowAdvancedTax,
    proofAttachments,
    isUploading,
    proofUploadUrl,
    deliverables,
    setDeliverables,
    isAlterationPrerequisiteFetching,
    baseInfluencerCommercial,
    baseBrandCommercial,
    influencerCostGstRate,
    influencerCostTdsRate,
    brandCostGstRate,
    commercialPreview,
    canSubmit,
    handleBeforeUpload,
    removeProofFile,
    handleConfirm,
    handleRemoveDeliverableIndex,
    isCreating,
    newInfluencerCommercial,
    newBrandCommercial,
    alterationPrerequisite,
    deletedPurchaseItems,
    briefInfluencer,
    campaignInfluencer,
  } = useChangeDeliverablesForm({
    campaignInfluencerId,
    briefId,
    onSuccess,
    viewOnly,
    alterationRequest,
  });

  const oldBrandServiceCostRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.brandCostServiceCostRate || 0
    : alterationRequest?.preCheckResult?.old?.brandCostServiceCostRate || 0;

  const oldBrandGstRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.brandCostGstRate || 0
    : alterationRequest?.preCheckResult?.old?.brandCostGstRate || 0;

  const newBrandServiceCostRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.new?.brandCostServiceCostRate || 0
    : alterationRequest?.preCheckResult?.new?.brandCostServiceCostRate || 0;

  const newBrandGstRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.new?.brandCostGstRate || 0
    : alterationRequest?.preCheckResult?.new?.brandCostGstRate || 0;

  const oldBrandBreakdown = useMemo(() => {
    return getBrandBreakdown(baseBrandCommercial, oldBrandServiceCostRate, oldBrandGstRate);
  }, [baseBrandCommercial, oldBrandServiceCostRate, oldBrandGstRate]);

  const newBrandBreakdown = useMemo(() => {
    return getBrandBreakdown(newBrandCommercial, newBrandServiceCostRate, newBrandGstRate);
  }, [newBrandCommercial, newBrandServiceCostRate, newBrandGstRate]);

  const brandBaseDelta = newBrandBreakdown.base - oldBrandBreakdown.base;
  const brandServiceCostDelta = newBrandBreakdown.serviceCost - oldBrandBreakdown.serviceCost;
  const brandGstDelta = newBrandBreakdown.gst - oldBrandBreakdown.gst;
  const brandNetChange = newBrandCommercial - baseBrandCommercial;

  const oldInfluencerCostGstRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.influencerCostGstRate || 0
    : alterationRequest?.preCheckResult?.old?.influencerCostGstRate || 0;

  const oldInfluencerCostTdsRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.influencerCostTdsRate || 0
    : alterationRequest?.preCheckResult?.old?.influencerCostTdsRate || 0;

  const oldInfluencerBreakdown = useMemo(() => {
    const gst = baseInfluencerCommercial * (oldInfluencerCostGstRate / 100);
    const tds = baseInfluencerCommercial * (oldInfluencerCostTdsRate / 100);
    const net = baseInfluencerCommercial + gst - tds;
    return { gst: Math.round(gst), tds: Math.round(tds), net: Math.round(net) };
  }, [baseInfluencerCommercial, oldInfluencerCostGstRate, oldInfluencerCostTdsRate]);

  const newInfluencerBreakdown = useMemo(() => {
    const gst = newInfluencerCommercial * (influencerCostGstRate / 100);
    const tds = newInfluencerCommercial * (influencerCostTdsRate / 100);
    const net = newInfluencerCommercial + gst - tds;
    return { gst: Math.round(gst), tds: Math.round(tds), net: Math.round(net) };
  }, [newInfluencerCommercial, influencerCostGstRate, influencerCostTdsRate]);

  const influencerBaseDelta = newInfluencerCommercial - baseInfluencerCommercial;
  const influencerGstDelta = newInfluencerBreakdown.gst - oldInfluencerBreakdown.gst;
  const influencerTdsDelta = newInfluencerBreakdown.tds - oldInfluencerBreakdown.tds;
  const influencerNetChange = newInfluencerBreakdown.net - oldInfluencerBreakdown.net;
  const allDeliverables = useMemo(() => {
    // For items in deliverables state, mark newly added items with isNewlyAdded: true
    const markedDeliverables = deliverables.map((item) => {
      if (!item.isExisting) {
        return {
          ...item,
          isNewlyAdded: true,
        };
      }
      return item;
    });

    return [...markedDeliverables];
  }, [deliverables]);

  const { deliverableConfiguratorsData } = useGetDeliverableConfigurators();
  const configurators = useMemo(() => {
    return deliverableConfiguratorsData?.data?.data || [];
  }, [deliverableConfiguratorsData]);

  const handleAddDeliverable = () => {
    const newItem: ILocalDeliverable = {
      clientSideId: Math.random().toString(36).substring(2, 9),
      purchaseItemType: "Content",
      contentCategory: "",
      contentSubType: {},
      contentType: "",
      adRightsDurations: "",
      adRightsNotes: "",
      adRightsType: "",
      adRightsDescription: "",
      contentDeliverableConfiguratorId: "",
      contentDeliverableConfiguratorDeliverableId: "",
      adRightsLinkedToContentPurchaseItemId: undefined,
      isExisting: false,
    };
    setDeliverables((prev) => [...prev, newItem]);
  };

  const handleRemoveDeliverable = (index: number) => {
    const itemToRemove = allDeliverables[index];
    if (itemToRemove && !itemToRemove.isDeleted) {
      const actualIndex = deliverables.findIndex((d) => d.clientSideId === itemToRemove.clientSideId);
      if (actualIndex !== -1) {
        handleRemoveDeliverableIndex(actualIndex);
      }
    }

    setDeliverables((prev) => {
      const removedId = itemToRemove?.clientSideId;
      const removedDbId = itemToRemove?._id;

      let updatedList: ILocalDeliverable[];
      if (itemToRemove.isExisting) {
        updatedList = prev.map((val) =>
          itemToRemove.clientSideId === val.clientSideId
            ? { ...val, isDeleted: true }
            : val
        );
      } else {
        updatedList = prev.filter((val) => val.clientSideId !== itemToRemove.clientSideId);
      }

      return updatedList.map((del) => {
        if (
          del.purchaseItemType === "Ad-Rights" &&
          !del.isExisting &&
          (del.adRightsLinkedToContentPurchaseItemId === removedId ||
            (removedDbId && del.adRightsLinkedToContentPurchaseItemId === removedDbId))
        ) {
          return {
            ...del,
            adRightsLinkedToContentPurchaseItemId: undefined,
          };
        }
        return del;
      });
    });
  };

  const handleSelectionChange = (index: number, value: string) => {
    const targetItem = allDeliverables[index];
    if (!targetItem) return;
    const actualIndex = deliverables.findIndex((d) => d.clientSideId === targetItem.clientSideId);
    if (actualIndex === -1) return;

    setDeliverables((prev) =>
      prev.map((del, i) => {
        if (i === actualIndex) {
          if (value === "Ad-Rights") {
            return {
              clientSideId: del.clientSideId || Math.random().toString(36).substring(2, 9),
              purchaseItemType: "Ad-Rights",
              contentCategory: "Ad Rights",
              contentSubType: {},
              contentType: "",
              adRightsDurations: "",
              adRightsNotes: "",
              adRightsType: "",
              adRightsDescription: "",
              contentDeliverableConfiguratorId: "",
              contentDeliverableConfiguratorDeliverableId: "",
              adRightsLinkedToContentPurchaseItemId: undefined,
              isExisting: del.isExisting,
            };
          }
          const [configuratorId, deliverableId] = value.split("|");
          const delConfig = configurators.find((cfg) => cfg._id === configuratorId);
          const format = delConfig?.deliverables.find((fmt) => fmt._id === deliverableId);

          return {
            clientSideId: del.clientSideId || Math.random().toString(36).substring(2, 9),
            purchaseItemType: "Content",
            contentCategory: format?.category || "",
            contentSubType: {},
            contentType: "",
            adRightsDurations: "",
            adRightsNotes: "",
            adRightsType: "",
            adRightsDescription: "",
            contentDeliverableConfiguratorId: configuratorId,
            contentDeliverableConfiguratorDeliverableId: deliverableId,
            adRightsLinkedToContentPurchaseItemId: undefined,
            isExisting: del.isExisting,
          };
        }
        return del;
      })
    );
  };

  const handleValueChange = (index: number, field: string, value: any) => {
    const targetItem = allDeliverables[index];
    if (!targetItem) return;
    const actualIndex = deliverables.findIndex((d) => d.clientSideId === targetItem.clientSideId);
    if (actualIndex === -1) return;

    setDeliverables((prev) =>
      prev.map((del, i) => {
        if (i === actualIndex) {
          const newDel = { ...del, [field]: value };
          if (field === "adRightsType" && value !== "Custom") {
            newDel.adRightsNotes = "";
          }
          return newDel;
        }
        return del;
      })
    );
  };

  const handleSubTypeValueChange = (index: number, subTypeName: string, value: string | undefined) => {
    const targetItem = allDeliverables[index];
    if (!targetItem) return;
    const actualIndex = deliverables.findIndex((d) => d.clientSideId === targetItem.clientSideId);
    if (actualIndex === -1) return;

    setDeliverables((prev) =>
      prev.map((del, i) => {
        if (i === actualIndex) {
          const newSubTypes = { ...del.contentSubType };
          if (value === undefined) {
            delete newSubTypes[subTypeName];
          } else {
            newSubTypes[subTypeName] = value;
          }
          return { ...del, contentSubType: newSubTypes };
        }
        return del;
      })
    );
  };

  const initialViewOnlyPurchaseItems = useMemo(() => {
    const isCompletedStatus =
      alterationRequest?.status === "Approved" || alterationRequest?.status === "Rejected";
    const source = isCompletedStatus
      ? alterationRequest?.preChangedDeliverablePurchaseItems
      : campaignInfluencer?.purchaseItems;
    return (source ?? []);
  }, [
    alterationRequest?.status,
    alterationRequest?.preChangedDeliverablePurchaseItems,
    campaignInfluencer?.purchaseItems,
  ]);

  return (
    <Form layout="vertical" onFinish={handleConfirm} className={styles.formContainer}>
      {/* Current Commercials */}
      <div className={styles.currentCommercialsCard}>
        <p className={styles.cardLabel}>Commercials</p>
        <div className={styles.grid}>
          <div>
            <p className={styles.valueLabel}>Current Influencer Commercial</p>
            <p className={styles.valueAmount}>
              {baseInfluencerCommercial != null ? formatInrDisplayAmount(baseInfluencerCommercial) : '—'}
            </p>
            <Form.Item label={<span className={styles.label}>Influencer commercial change</span>} style={{ marginBottom: 0 }}>
              <div className={styles.inputWithDirection}>
                <div className={styles.arrowButtons}>
                  <Button
                    className={`${styles.arrowBtn} ${influencerDirection === 'add' ? styles.activeUp : ''}`}
                    onClick={viewOnly ? undefined : () => setInfluencerDirection('add')}
                    disabled={viewOnly}
                    icon={<ArrowUp size={16} />}
                    size='small'
                  />
                  <Button
                    className={`${styles.arrowBtn} ${influencerDirection === 'subtract' ? styles.activeDown : ''}`}
                    onClick={viewOnly ? undefined : () => setInfluencerDirection('subtract')}
                    disabled={viewOnly}
                    icon={<ArrowDown size={16} />}
                    size='small'
                  />
                </div>
                <div className={styles.inputWrapper}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Input
                      type="number"
                      min={0}
                      value={influencerDelta}
                      onChange={(e) => setInfluencerDelta(e.target.value)}
                      placeholder="₹ 0"
                      disabled={viewOnly}
                    />
                  </Form.Item>
                </div>
              </div>
              {baseInfluencerCommercial != null && Number(influencerDelta) > 0 && (
                <div style={{ marginTop: 4, fontSize: '12px', color: '#697386' }}>
                  {`₹${baseInfluencerCommercial.toLocaleString('en-IN')} ${influencerDirection === 'add' ? '+' : '-'} ₹${Number(influencerDelta).toLocaleString('en-IN')} = `}
                  <span style={{ color: influencerDirection === 'add' ? '#16a34a' : '#e11d48', fontWeight: 600 }}>
                    ₹{(influencerDirection === 'add' ? baseInfluencerCommercial + Number(influencerDelta) : baseInfluencerCommercial - Number(influencerDelta)).toLocaleString('en-IN')}
                  </span>
                </div>
              )}
            </Form.Item>
          </div>
          <div>
            <p className={styles.valueLabel}>Current Brand Commercial</p>
            <p className={styles.valueAmount}>
              {baseBrandCommercial != null ? formatInrDisplayAmount(baseBrandCommercial) : '—'}
            </p>
            <Form.Item label={<span className={styles.label}>Brand commercial change</span>} style={{ marginBottom: 0 }}>
              <div className={styles.inputWithDirection}>
                <div className={styles.arrowButtons}>
                  <Button
                    className={`${styles.arrowBtn} ${brandDirection === 'add' ? styles.activeUp : ''}`}
                    onClick={viewOnly ? undefined : () => setBrandDirection('add')}
                    disabled={viewOnly}
                    icon={<ArrowUp size={16} />}
                    size='small'
                  />
                  <Button
                    className={`${styles.arrowBtn} ${brandDirection === 'subtract' ? styles.activeDown : ''}`}
                    onClick={viewOnly ? undefined : () => setBrandDirection('subtract')}
                    disabled={viewOnly}
                    icon={<ArrowDown size={16} />}
                    size='small'
                  />
                </div>
                <div className={styles.inputWrapper}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Input
                      type="number"
                      min={0}
                      value={brandDelta}
                      onChange={(e) => setBrandDelta(e.target.value)}
                      placeholder="₹ 0"
                      disabled={viewOnly}
                    />
                  </Form.Item>
                </div>
              </div>
              {baseBrandCommercial != null && Number(brandDelta) > 0 && (
                <div style={{ marginTop: 4, fontSize: '12px', color: '#697386' }}>
                  {`₹${baseBrandCommercial.toLocaleString('en-IN')} ${brandDirection === 'add' ? '+' : '-'} ₹${Number(brandDelta).toLocaleString('en-IN')} = `}
                  <span style={{ color: brandDirection === 'add' ? '#16a34a' : '#e11d48', fontWeight: 600 }}>
                    ₹{(brandDirection === 'add' ? baseBrandCommercial + Number(brandDelta) : baseBrandCommercial - Number(brandDelta)).toLocaleString('en-IN')}
                  </span>
                </div>
              )}
            </Form.Item>
          </div>
        </div>
        {/* Advanced Tax Accordion */}
      <div style={{marginTop: 10}}>
        <button
          type="button"
          className={styles.advancedTaxToggle}
          onClick={() => setShowAdvancedTax(!showAdvancedTax)}
        >
          {showAdvancedTax ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          Advanced tax settings
        </button>
        {showAdvancedTax && (
          <div className={styles.advancedTaxContainer} style={{ padding: '12px' }}>
            <Flex gap={16} align="stretch" style={{ width: '100%' }}>
              {/* Creator Tax Section */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span className={styles.label} style={{ fontSize: '11px', fontWeight: 700, color: '#697386', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Creator
                </span>

                <div className={styles.taxRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Checkbox
                    checked={tax.creatorGstEnabled}
                    onChange={(e) =>
                      setTax((prev) => ({
                        ...prev,
                        creatorGstEnabled: e.target.checked,
                      }))
                    }
                    disabled={viewOnly}
                  >
                    <span style={{ fontSize: '13px', color: '#1a1f36', marginLeft: 4 }}>GST on creator commercial</span>
                  </Checkbox>
                  <span style={{ fontWeight: 500, fontSize: '13px', color: '#1a1f36' }}>{`${tax.creatorGstPercent ?? 18}%`}</span>
                </div>

                <div className={styles.taxRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Checkbox
                    checked={tax.creatorTdsEnabled}
                    onChange={(e) =>
                      setTax((prev) => ({
                        ...prev,
                        creatorTdsEnabled: e.target.checked,
                      }))
                    }
                    disabled={viewOnly}
                  >
                    <span style={{ fontSize: '13px', color: '#1a1f36', marginLeft: 4 }}>TDS on creator commercial</span>
                  </Checkbox>
                  <span style={{ fontWeight: 500, fontSize: '13px', color: '#1a1f36' }}>{`${tax.creatorTdsPercent ?? influencerCostTdsRate ?? 10}%`}</span>
                </div>
              </div>

              {/* Vertical Divider */}
              <Divider type="vertical" style={{ height: 'auto', margin: 0, borderColor: '#e3e8ee' }} />

              {/* Brand Tax Section */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span className={styles.label} style={{ fontSize: '11px', fontWeight: 700, color: '#697386', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Brand
                </span>

                <div className={styles.taxRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Checkbox
                    checked={tax.brandGstEnabled}
                    onChange={(e) =>
                      setTax((prev) => ({
                        ...prev,
                        brandGstEnabled: e.target.checked,
                      }))
                    }
                    disabled={viewOnly}
                  >
                    <span style={{ fontSize: '13px', color: '#1a1f36', marginLeft: 4 }}>GST on brand commercial</span>
                  </Checkbox>
                  <span style={{ fontWeight: 500, fontSize: '13px', color: '#1a1f36' }}>{`${tax.brandGstPercent ?? brandCostGstRate ?? 18}%`}</span>
                </div>

                <div className={styles.taxRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Checkbox
                    checked={tax.brandServiceCostEnabled}
                    onChange={(e) =>
                      setTax((prev) => ({
                        ...prev,
                        brandServiceCostEnabled: e.target.checked,
                      }))
                    }
                    disabled={viewOnly}
                  >
                    <span style={{ fontSize: '13px', color: '#1a1f36', marginLeft: 4 }}>Brand Service Cost</span>
                  </Checkbox>
                  <span style={{ fontWeight: 500, fontSize: '13px', color: '#1a1f36' }}>{`${tax.brandServiceCostRate ?? 0}%`}</span>
                </div>
              </div>
            </Flex>
          </div>
        )}
      </div>
      </div>
      {/* Deliverables Input List using our new modular Component */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
        <span className={styles.label} style={{ fontSize: '13px', color: '#1a1f36' }}>Deliverables</span>
        {!viewOnly ? <Card styles={{
          body: {
            padding: 10
          }
        }}> <DeliverableFormats
          deliverables={allDeliverables}
          onChange={(updatedList) => {
            const activeList = updatedList.filter((item) => !item.isDeleted);
            setDeliverables(activeList);
          }}
          onAdd={handleAddDeliverable}
          onRemove={handleRemoveDeliverable}
          onSelectionChange={handleSelectionChange}
          onValueChange={handleValueChange}
          onSubTypeValueChange={handleSubTypeValueChange}
          disabled={viewOnly}
          disableEditingExisting={true}
        />
        </Card> : initialViewOnlyPurchaseItems.length > 0 ? <Flex align="center">
                <DeliverablePillSummary deliverable={{
                  briefId: briefId || campaignInfluencer?.briefId || "",
                  purchaseItems: initialViewOnlyPurchaseItems,
                }}/>
                →
                <DeliverablePillSummary deliverable={{
                  briefId: briefId || campaignInfluencer?.briefId || "",
                  purchaseItems: [
                    ...initialViewOnlyPurchaseItems
                      .map((item) =>
                        alterationRequest?.cancelledDeliverablePurchaseItemIds?.includes(item._id ?? "")
                          ? { ...item, isCancelled: true }
                          : item
                      ),
                    ...(alterationRequest?.addDeliverablePurchaseItems ?? []),
                  ]
                }}/>
                </Flex> : <Empty description="Deliverables not found" style={{width: "100%"}}/>}
      </div>
      {/* Remarks */}
      <Form.Item label={<span className={styles.label}>Remarks</span>} style={{ marginBottom: 0 }}>
        <Input.TextArea
          className={styles.textarea}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Reason for this change..."
          rows={3}
          disabled={viewOnly}
        />
      </Form.Item>

      {/* Proof of purchase */}
      {!viewOnly && (
        <Form.Item
          label={<span className={styles.label}>Proof of Purchase</span>}
          required
          style={{ marginBottom: 0 }}
        >
          <Upload.Dragger
            name="file"
            multiple={false}
            beforeUpload={handleBeforeUpload}
            fileList={proofAttachments.length ? [{ uid: proofAttachments[0].id, name: proofAttachments[0].name, url: proofUploadUrl }] : []}
            onRemove={(file) => {
              removeProofFile(file.uid);
            }}
            showUploadList={true}
            accept=".pdf,.png,.jpg,.jpeg"
            maxCount={1}
            disabled={!!viewOnly}
          >
            <div className={styles.uploadPrompt}>
              <UploadCloud size={24} className={styles.icon} />
              <p className={styles.promptTitle}>
                <span>Click to upload</span> or drag and drop
              </p>
              <p className={styles.promptSub}>PDF, PNG, JPG up to 10MB</p>
            </div>
          </Upload.Dragger>
        </Form.Item>
      )}

      {viewOnly && (
        <Button
          type='link'
          size='small'
          onClick={() => {
            window.open(alterationRequest?.proofUploadUrl, "_blank");
          }}
          className={styles.advancedTaxToggle}
          style={{ width: "fit-content" }}
        >
          Proof of Alteration
        </Button>
      )}

      {/* Preview Summary */}
      {!viewOnly && deliverables.length > 0 && <div className={styles.previewCard}>
        <p className={styles.previewTitle}>Preview</p>
        {/* {isAlterationPrerequisiteFetching ? (
          <Skeleton active />
        ) : ( */}
          <Flex vertical gap={12}>
            {/* <div className={styles.previewContainer}>
              <div className={styles.previewColumn}>
                <h4 className={styles.columnTitle}>Influencer Ledger</h4>
                <div className={styles.previewRow}>
                  <span className={styles.previewLabel}>New commercials</span>
                  <span className={styles.previewValue}>
                    {`${formatSignedInr(influencerBaseDelta)}`}
                  </span>
                </div>
                <div className={styles.previewRow}>
                  <span className={styles.previewLabel}>GST</span>
                  <span className={styles.previewValue}>{formatSignedInr(influencerGstDelta)}</span>
                </div>
                <div className={styles.previewRow}>
                  <span className={styles.previewLabel}>TDS</span>
                  <span className={styles.previewValue}>{formatSignedInr(influencerTdsDelta ? -influencerTdsDelta : influencerTdsDelta)}</span>
                </div>
                <Divider className={styles.previewDivider} />
                <div className={styles.previewRowTotal}>
                  <span className={styles.previewLabelTotal}>Total net change</span>
                  <span className={styles.previewValueTotal}>{formatSignedInr(influencerNetChange)}</span>
                </div>
              </div>

              <div className={styles.verticalDivider} />

              <div className={styles.previewColumn}>
                <h4 className={styles.columnTitle}>Brand Ledger</h4>
                <div className={styles.previewRow}>
                  <span className={styles.previewLabel}>New Commercials</span>
                  <span className={styles.previewValue}>
                    {`${formatSignedInr(brandBaseDelta)}`}
                  </span>
                </div>
                <div className={styles.previewRow}>
                  <span className={styles.previewLabel}>GST</span>
                  <span className={styles.previewValue}>{formatSignedInr(brandGstDelta)}</span>
                </div>
                <div className={styles.previewRow}>
                  <span className={styles.previewLabel}>Service cost</span>
                  <span className={styles.previewValue}>{formatSignedInr(brandServiceCostDelta)}</span>
                </div>
                <Divider className={styles.previewDivider} />
                <div className={styles.previewRowTotal}>
                  <span className={styles.previewLabelTotal}>Total net change</span>
                  <span className={styles.previewValueTotal}>{formatSignedInr(brandNetChange)}</span>
                </div>
              </div>
            </div> */}
              <>
                <Divider className={styles.previewDivider} style={{ margin: '8px 0 0 0' }} />
                <div>
                  <span className={styles.previewLabel}>Deliverable changes</span>
                  {/* <span className={styles.previewValue}>{changedLabels.join(', ')}</span> */}
                  <Flex align="center">
                <DeliverablePillSummary deliverable={{
                  briefId: briefId || campaignInfluencer?.briefId || "",
                  purchaseItems: initialViewOnlyPurchaseItems,
                }}/>
                →
                <DeliverablePillSummary deliverable={{
                  briefId: briefId || campaignInfluencer?.briefId || "",
                  purchaseItems: deliverables.map((val) => ({...val, _id: val.clientSideId, isCancelled: val.isCancelled || val.isDeleted}))
                }}/>
                </Flex>
                </div>
              </>
          </Flex>
        {/* )} */}
      </div>}

      {!viewOnly && (
        <div className={styles.footer}>
          <button type="button" onClick={onCancel} className={styles.cancelBtn}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || isCreating || isUploading}
            className={styles.confirmBtn}
          >
            Apply Changes
          </button>
        </div>
      )}
    </Form>
  );
};
